/**
 * Anonymous restaurant saves — the device-local half of anon-first saving.
 *
 * An anonymous save succeeds IMMEDIATELY: the durable record is the backend
 * GuestEngagementAction row written by persistAnonSave (keyed to the never-rotated anon_id,
 * deduped per restaurant, promoted into real favorites by the points claim on login). This
 * module only keeps the ALIASES locally so the bookmark icon renders "saved" across reloads
 * without a backend read. localStorage (not sessionStorage) on purpose: it must survive the
 * OAuth redirect and app restarts — the sessionStorage intent this replaces did not, which is
 * why anonymous save completion collapsed to ~1 user/month.
 */
const ANON_SAVED_ALIASES_KEY = 'rsly_anon_saved_aliases'
const MAX_TRACKED = 200

function read(): string[] {
  try {
    const raw = localStorage.getItem(ANON_SAVED_ALIASES_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((a) => typeof a === 'string') : []
  } catch {
    return []
  }
}

function write(aliases: string[]): void {
  try {
    localStorage.setItem(
      ANON_SAVED_ALIASES_KEY,
      JSON.stringify(aliases.slice(-MAX_TRACKED)),
    )
  } catch {
    // Storage unavailable — the backend record still exists; only icon state is lost.
  }
}

export function getAnonSavedAliases(): string[] {
  return read()
}

export function isAnonSaved(alias?: string | null): boolean {
  return !!alias && read().includes(alias)
}

export function addAnonSavedAlias(alias: string): void {
  const aliases = read()
  if (!aliases.includes(alias)) {
    aliases.push(alias)
    write(aliases)
  }
}

/**
 * Called after login once the claim has merged the device's saves into the account. The local
 * list's job is done — the account's favorites are now the source of truth.
 */
export function clearAnonSavedAliases(): void {
  try {
    localStorage.removeItem(ANON_SAVED_ALIASES_KEY)
  } catch {
    // ignore
  }
}
